import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { IconButton } from '@eventbrite/eds-icon-button';
import {
    ArrowRightChunky as ArrowRightChunkySvg,
    CurrentLocation,
    MapPinFill as MapPinFillSvg,
} from '@eventbrite/eds-iconography';
import { addWindowListener, removeWindowListener } from '@eventbrite/eds-utils';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import debounce from 'lodash/debounce';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import React, { PureComponent } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
    ALL_EVENTS_TYPE,
    CATEGORY_DROPDOWN_SELECT,
    EVENTS_TYPE,
} from '../../../../../../constants';
//TODO: Clean up shared containers to named imports
import { addNavigatorSearchTerm } from '@eventbrite/personalization';
import { LocationAutocompleteContainer } from '../../../../../../containers/LocationAutocompleteContainer';
import { SuggestionsState } from '../../../../../../types/index';
import { SearchAutocomplete } from '../SearchAutocomplete';
import './searchInput.scss';

const LOCATION_DEBOUNCE_INTERVAL = 200;
const PAGE_SHOW_EVENT_NAME = 'pageshow';

interface LocationInputSuffixProps {
    isUsingCurrentLocation: boolean;
    handleUseCurrentLocation?: Function;
}
const LocationInputSuffix: React.FunctionComponent<LocationInputSuffixProps> = (
    props,
) => {
    return (
        <>
            <div className="eds-show-down-mw">
                <IconButton
                    style={props.isUsingCurrentLocation ? 'link' : 'neutral'}
                    iconType={<CurrentLocation />}
                    onClick={props.handleUseCurrentLocation}
                    title={
                        props.isUsingCurrentLocation
                            ? gettext('Using current location')
                            : gettext('Use my current location')
                    }
                />
            </div>
        </>
    );
};

interface SearchInputProps {
    handleApplyFilter?: Function;
    handleLocationSuggestionChange?: Function;
    handleSearchSuggestionChange?: Function;
    currentPlace?: string;
    initialSearchQuery?: string;
    suggestions: SuggestionsState;
    handleLocationSelect?: Function;
    handleUseCurrentLocation?: Function;
    onGoogleMapsLoaded?: Function;
    hasHistory?: boolean;
    searchEventSuggestionsAction?: Function;
    trackGAEvent?: Function;
    history: RouteComponentProps['history'];
    location: object;
    isUsingCurrentLocation: boolean;
}

interface SearchInputState {
    searchQuery?: string;
    shouldFocusInput: boolean;
}

export class SearchInput extends PureComponent<
    SearchInputProps,
    SearchInputState
> {
    private _boundHandlePageLoadFromCache: (event: any) => void;
    constructor(props: Readonly<SearchInputProps>) {
        super(props);

        this.state = {
            searchQuery: props.initialSearchQuery,
            shouldFocusInput: false,
        };

        this._boundHandlePageLoadFromCache = this._handlePageLoadFromCache;
    }

    componentDidMount() {
        addWindowListener(
            PAGE_SHOW_EVENT_NAME,
            this._boundHandlePageLoadFromCache,
        );
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps({
        initialSearchQuery: newInitialSearchQuery,
    }: any) {
        if (newInitialSearchQuery !== this.props.initialSearchQuery) {
            this.setState({ searchQuery: newInitialSearchQuery });
        }
    }

    componentWillUnmount() {
        removeWindowListener(
            PAGE_SHOW_EVENT_NAME,
            this._boundHandlePageLoadFromCache,
        );
    }

    /**
     * Helper function which consumes the `pageshow` event to determine if the
     * page was served from the browser cache. Allows for a hook such that any
     * state resetting or other behaviors can occur.
     */
    _handlePageLoadFromCache = (event: { persisted: boolean }) => {
        const { currentPlace, initialSearchQuery } = this.props;

        if (event.persisted) {
            this.setState({
                searchQuery: initialSearchQuery,
            });

            //For browsers which utilize back-forward cache, the store is cached with the
            //autocomplete suggestions previously entered by the user so we want to display
            //suggestions which make sense in the context of the current state.
            this._requestLocationSuggestions(currentPlace);
        }
    };

    _handleSubmit = () => {
        const { handleApplyFilter, hasHistory } = this.props;

        let { searchQuery } = this.state;

        let newQuery = searchQuery;

        if (hasHistory && HAS_WINDOW && searchQuery) {
            addNavigatorSearchTerm(searchQuery);
        }

        // when searching for 'events' or 'all events' input should be resetted
        if (includes([EVENTS_TYPE, ALL_EVENTS_TYPE], searchQuery)) {
            newQuery = '';
        }

        // when searching term matches category dropdown input and query should be resetted and
        if (searchQuery === CATEGORY_DROPDOWN_SELECT) {
            searchQuery = '';
            newQuery = '';
        }

        this.setState(
            {
                searchQuery: newQuery,
            },
            () => {
                // Search input should always reset the pagination
                handleApplyFilter?.({ q: searchQuery, page: 1 });
            },
        );
    };

    _handleSearchSuggestionChange = (searchQuery: string) => {
        const { handleSearchSuggestionChange } = this.props;

        handleSearchSuggestionChange?.(searchQuery);
    };

    _handleApplySearchFilter = (searchFilter: {
        q: string;
        tags: string[];
    }) => {
        const { handleApplyFilter } = this.props;
        const { searchQuery } = this.state;

        let newQuery = searchQuery;
        const { q, tags } = searchFilter;

        if (q) {
            newQuery = q;
        }

        // Special case where the user selects a category from the
        // dropdown. Need to ensure a re-render occurs so utilizing
        // an allow-listed value to pass through.
        if (q === '' && tags) {
            newQuery = CATEGORY_DROPDOWN_SELECT;

            if (searchQuery === CATEGORY_DROPDOWN_SELECT) {
                newQuery = '';
            }
        }

        // when searching for 'events' or 'all events' input should be resetted
        if (includes([EVENTS_TYPE, ALL_EVENTS_TYPE], (q || '').toLowerCase())) {
            newQuery = '';
        }

        this.setState(
            {
                searchQuery: newQuery,
            },
            () => handleApplyFilter?.(searchFilter),
        );
    };

    _handleSearchQueryChange = (searchQuery: string) => {
        this.setState({ searchQuery });
    };

    _handleSearchFieldFocus = () => {
        if (!this.state.searchQuery && isEmpty(this.props.suggestions.events)) {
            this.props.searchEventSuggestionsAction?.();
        }

        this.setState({
            shouldFocusInput: true,
        });
    };

    _handleSearchFieldBlur = () => {
        this.setState({
            shouldFocusInput: false,
        });
    };

    _handleClearButtonClick = () => {
        this.setState({
            searchQuery: '',
            shouldFocusInput: true,
        });
    };

    _handleLocationSelection = (place: string) => {
        const { handleLocationSelect } = this.props;

        if (this.state.searchQuery !== this.props.initialSearchQuery) {
            handleLocationSelect?.(place, this.state.searchQuery);
        } else {
            handleLocationSelect?.(place);
        }
    };

    _requestLocationSuggestions = debounce((locationQuery) => {
        const { handleLocationSuggestionChange } = this.props;

        handleLocationSuggestionChange?.(locationQuery);
    }, LOCATION_DEBOUNCE_INTERVAL);

    render() {
        const {
            suggestions,
            hasHistory,
            trackGAEvent,
            handleUseCurrentLocation,
            history,
            isUsingCurrentLocation,
        } = this.props;
        const { searchQuery, shouldFocusInput } = this.state;

        const mapIcon = (
            <Icon color="grey-800" size="small" type={<MapPinFillSvg />} />
        );

        const LocationSuffix = (
            <LocationInputSuffix
                isUsingCurrentLocation={isUsingCurrentLocation}
                handleUseCurrentLocation={handleUseCurrentLocation}
            />
        );

        return (
            <form autoComplete="none">
                <nav className="search-input">
                    <div className="search-input__query">
                        <div className="search-input__query__container">
                            <SearchAutocomplete
                                hasHistory={hasHistory}
                                searchQuery={searchQuery}
                                suggestions={suggestions}
                                onSearchSuggestionChange={
                                    this._handleSearchSuggestionChange
                                }
                                onSearchQueryChange={
                                    this._handleSearchQueryChange
                                }
                                onSearchFilterChange={
                                    this._handleApplySearchFilter
                                }
                                onClearButtonClick={
                                    this._handleClearButtonClick
                                }
                                onFocus={this._handleSearchFieldFocus}
                                onBlur={this._handleSearchFieldBlur}
                                shouldFocusInput={shouldFocusInput}
                                trackGAEvent={trackGAEvent}
                                history={history}
                            />
                            <LocationAutocompleteContainer
                                hideOnline={true}
                                handleUseCurrentLocation={
                                    handleUseCurrentLocation
                                }
                                handleLocationSelect={
                                    this._handleLocationSelection
                                }
                                history={history}
                                prefix={mapIcon}
                                suffix={LocationSuffix}
                            />
                        </div>

                        <div className="eds-l-mar-bot-5 eds-show-up-sw">
                            <Button
                                data-spec="search-button"
                                onClick={this._handleSubmit}
                                style="neutral"
                                type="button"
                            >
                                {gettext('Search')}
                            </Button>
                        </div>
                        <div className="eds-l-mar-bot-5 eds-show-down-sm">
                            <Button
                                style="neutral"
                                type="button"
                                onClick={this._handleSubmit}
                                __containerClassName="search-input__query-submit"
                                title="Run Search"
                            >
                                <Icon
                                    type={<ArrowRightChunkySvg />}
                                    strokeColor="ui-800"
                                />
                            </Button>
                        </div>
                    </div>
                    <div className="search-input__divider eds-l-mar-2" />
                </nav>
            </form>
        );
    }
}
